import { useState, useEffect, useCallback } from 'react';
import moment from 'moment';
import { DatePicker, Text, TimePicker } from 'components';
import { ESendTime, TMessageSendOption } from 'types';
import { sendAllMessageStrings } from 'constants/strings';

type Props = {
  sendOption: TMessageSendOption;
  onSendOptionChange: (option: Partial<TMessageSendOption>) => void;
  className?: string;
  /** 配信予約の編集では即時配信へ切り替えさせない */
  disableImmediate?: boolean;
};

const STEP_MINUTES = 5;
const STEP_SECONDS = STEP_MINUTES * 60;
const contentHeaderStrings = sendAllMessageStrings.contentHeader;

const roundToNearestStepMinutes = (time: string): string => {
  const [hours, minutes] = time.split(':').map(Number);

  const roundedMins = Math.floor(minutes / STEP_MINUTES) * STEP_MINUTES;

  return `${String(hours).padStart(2, '0')}:${String(roundedMins).padStart(2, '0')}`;
};

function SendTimeSelector({
  sendOption,
  onSendOptionChange,
  className,
  disableImmediate = false,
}: Props) {
  const [selectedDate, setSelectedDate] = useState<Date | null>(
    sendOption.scheduledDateTime
      ? moment(sendOption.scheduledDateTime).toDate()
      : null,
  );
  const [selectedTime, setSelectedTime] = useState<string>(() => {
    if (sendOption.scheduledDateTime) {
      const time = moment(sendOption.scheduledDateTime).format('HH:mm');
      return roundToNearestStepMinutes(time);
    }
    return '12:00';
  });

  useEffect(() => {
    if (!sendOption.scheduledDateTime) return;

    const date = moment(sendOption.scheduledDateTime).toDate();
    setSelectedDate(date);
    const time = moment(sendOption.scheduledDateTime).format('HH:mm');
    setSelectedTime(roundToNearestStepMinutes(time));
  }, [sendOption.scheduledDateTime]);

  const getMinTime = useCallback(
    (forDate?: Date) => {
      const dateToCheck =
        forDate ?? selectedDate ?? moment().add(1, 'day').toDate();
      const isToday = moment(dateToCheck).isSame(moment(), 'day');

      if (isToday) {
        const now = moment();
        const currentTime = now.format('HH:mm');
        const roundedTime = roundToNearestStepMinutes(currentTime);
        const roundedMoment = moment(roundedTime, 'HH:mm');

        if (roundedMoment.isBefore(now)) {
          return roundedMoment.add(STEP_MINUTES, 'minutes').format('HH:mm');
        }

        return roundedTime;
      }

      return undefined;
    },
    [selectedDate],
  );

  const isValidDate = useCallback((currentDate: moment.Moment): boolean => {
    return currentDate.isSameOrAfter(moment(), 'day');
  }, []);

  const handleDateChange = useCallback(
    (date: moment.Moment | string) => {
      const momentDate = moment.isMoment(date) ? date : moment(date);
      const dateValue = momentDate.toDate();
      setSelectedDate(dateValue);

      const roundedTime = roundToNearestStepMinutes(selectedTime);
      const [hours, minutes] = roundedTime.split(':');
      const combinedDateTime = moment({
        year: momentDate.year(),
        month: momentDate.month(),
        date: momentDate.date(),
        hour: parseInt(hours),
        minute: parseInt(minutes),
        second: 0,
        millisecond: 0,
      }).toDate();

      if (momentDate.isSame(moment(), 'day')) {
        const now = moment();
        const selectedMoment = moment(combinedDateTime);
        if (selectedMoment.isBefore(now)) {
          const minTime = getMinTime(dateValue);
          if (minTime) {
            setSelectedTime(minTime);
            const [minHours, minMinutes] = minTime.split(':');
            const validDateTime = moment({
              year: momentDate.year(),
              month: momentDate.month(),
              date: momentDate.date(),
              hour: parseInt(minHours),
              minute: parseInt(minMinutes),
              second: 0,
              millisecond: 0,
            }).toDate();
            onSendOptionChange({ scheduledDateTime: validDateTime, sendTime: ESendTime.SCHEDULED });
            return;
          }
        }
      }

      setSelectedTime(roundedTime);
      onSendOptionChange({ scheduledDateTime: combinedDateTime, sendTime: ESendTime.SCHEDULED });
    },
    [selectedTime, onSendOptionChange, getMinTime],
  );

  const handleTimeChange = useCallback(
    (time: string) => {
      const roundedTime = roundToNearestStepMinutes(time);

      const dateToUse = selectedDate || moment().add(1, 'day').toDate();
      const momentDate = moment(dateToUse);
      const [hours, minutes] = roundedTime.split(':');

      const combinedDateTime = moment({
        year: momentDate.year(),
        month: momentDate.month(),
        date: momentDate.date(),
        hour: parseInt(hours),
        minute: parseInt(minutes),
        second: 0,
        millisecond: 0,
      }).toDate();

      if (momentDate.isSame(moment(), 'day')) {
        const now = moment();
        const selectedMoment = moment(combinedDateTime);

        if (selectedMoment.isBefore(now)) {
          const minTime = getMinTime(dateToUse);

          if (minTime) {
            setSelectedTime(minTime);
            const [minHours, minMinutes] = minTime.split(':');
            const validDateTime = moment({
              year: momentDate.year(),
              month: momentDate.month(),
              date: momentDate.date(),
              hour: parseInt(minHours),
              minute: parseInt(minMinutes),
              second: 0,
              millisecond: 0,
            }).toDate();
            onSendOptionChange({ scheduledDateTime: validDateTime, sendTime: ESendTime.SCHEDULED });
            return;
          }
        }
      }

      setSelectedTime(roundedTime);

      if (!selectedDate) {
        setSelectedDate(dateToUse);
      }

      onSendOptionChange({ scheduledDateTime: combinedDateTime, sendTime: ESendTime.SCHEDULED });
    },
    [selectedDate, getMinTime, onSendOptionChange],
  );

  const handleScheduledRadioChange = useCallback(() => {
    const tomorrow = moment().add(1, 'day');
    const defaultDateTime = moment({
      year: tomorrow.year(),
      month: tomorrow.month(),
      date: tomorrow.date(),
      hour: 12,
      minute: 0,
      second: 0,
      millisecond: 0,
    }).toDate();
    setSelectedDate(defaultDateTime);
    setSelectedTime('12:00');
    onSendOptionChange({
      sendTime: ESendTime.SCHEDULED,
      scheduledDateTime: defaultDateTime,
    });
  }, [onSendOptionChange]);

  return (
    <div
      className={`flex flex-wrap gap-4 pt-4 ${className || ''}`}
    >
      <Text className="py-2" bold>
        {sendAllMessageStrings.contentHeader.sendTime.label}
      </Text>

      <div className="flex flex-col gap-4 pl-0 py-2 sm:pl-4">
        <label
          className={`flex items-center gap-2 ${disableImmediate ? 'cursor-not-allowed' : 'cursor-pointer'}`}
        >
          <input
            type="radio"
            name="sendTime"
            value={ESendTime.IMMEDIATE}
            checked={sendOption.sendTime === ESendTime.IMMEDIATE}
            disabled={disableImmediate}
            onChange={() => {
              onSendOptionChange({ sendTime: ESendTime.IMMEDIATE });
            }}
            className="w-4 h-4 text-primary focus:ring-primary disabled:opacity-50"
          />
          <Text className={disableImmediate ? 'text-muted' : undefined}>
            {contentHeaderStrings.sendTime.immediate}
          </Text>
        </label>

        <div className="flex items-start gap-3 sm:gap-4">
          <label className="flex items-center gap-2 cursor-pointer pt-2">
            <input
              type="radio"
              name="sendTime"
              value={ESendTime.SCHEDULED}
              checked={sendOption.sendTime === ESendTime.SCHEDULED}
              onChange={handleScheduledRadioChange}
              className="w-4 h-4 text-primary focus:ring-primary"
            />
          </label>
          <div className="grid w-full min-w-0 grid-cols-1 gap-3 sm:flex sm:w-auto sm:flex-1 sm:flex-wrap sm:items-end sm:gap-4">
            <div className="w-full sm:flex-1">
              <DatePicker
                value={selectedDate || moment().add(1, 'day').toDate()}
                onChange={handleDateChange}
                isValidDate={isValidDate}
                inputProps={{
                  placeholder: contentHeaderStrings.sendTime.datePlaceholder,
                }}
                format="YYYY/MM/DD"
              />
            </div>

            <div className="w-full sm:flex-1">
              <TimePicker
                value={selectedTime}
                onChange={handleTimeChange}
                min={getMinTime()}
                step={STEP_SECONDS}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default SendTimeSelector;
